declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: (...args: any[]) => void;
    __metaPixelInitialized?: boolean;
  }
}

type MetaEventParams = Record<string, unknown>;
type MetaEventOptions = {
  eventId?: string;
};

const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const META_SCRIPT_ID = "meta-pixel-script";

/**
 * Checks if the code is executing in a browser environment.
 *
 * @returns True if running in the browser, false otherwise.
 */
function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

/**
 * Determines whether the Meta Pixel tracking is enabled.
 * Requires both a browser environment and a valid Pixel ID configured via environment variables.
 *
 * @returns True if the Pixel is configured and can run.
 */
export function isMetaPixelEnabled(): boolean {
  return isBrowser() && Boolean(META_PIXEL_ID);
}

/**
 * Initializes the Meta Pixel script and tracking queue on the page.
 * This is an idempotent operation; it safely skips execution if the Pixel is already initialized or disabled.
 *
 * Note: Disables automatic page view tracking and microdata extraction to enforce explicit, privacy-compliant tracking.
 */
export function initializeMetaPixel(): void {
  if (!isMetaPixelEnabled() || !META_PIXEL_ID) {
    return;
  }

  if (!window.fbq) {
    const fbq =
      ((...args: any[]) => {
        (fbq as any).callMethod
          ? (fbq as any).callMethod(...args)
          : (fbq as any).queue.push(args);
      }) as any;

    if (!window._fbq) {
      window._fbq = fbq;
    }

    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    fbq.disablePushState = true; // Disable automatic PageView tracking on route changes
    window.fbq = fbq;
  }

  if (!document.getElementById(META_SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = META_SCRIPT_ID;
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  }

  if (!window.__metaPixelInitialized) {
    window.fbq?.("set", "autoConfig", false, META_PIXEL_ID); // Disable automatic tracking like Microdata or Button clicks
    window.fbq?.("init", META_PIXEL_ID);
    window.__metaPixelInitialized = true;
  }
}

/**
 * Tracks a standard Meta Pixel event (e.g., 'ViewContent', 'AddToCart', 'Purchase').
 * Automatically initializes the Pixel if it has not been loaded yet.
 *
 * @param eventName - The standard event name defined by Meta.
 * @param params - Optional data (like currency, value) conforming to Meta's standard event specifications.
 *
 * @example
 * trackMetaStandardEvent('Purchase', { value: 29.99, currency: 'EUR' });
 */
export function trackMetaStandardEvent(eventName: string, params?: MetaEventParams, eventOptions?: MetaEventOptions): void {
  if (!isMetaPixelEnabled()) {
    return;
  }

  initializeMetaPixel();
  const options = buildMetaEventOptions(eventOptions);
  if (options) {
    window.fbq?.("track", eventName, params ?? {}, options);
    return;
  }

  window.fbq?.("track", eventName, params ?? {});
}

/**
 * Tracks a custom Meta Pixel event not covered by standard events.
 * Automatically initializes the Pixel if it has not been loaded yet.
 *
 * @param eventName - The custom name of the event.
 * @param params - Optional free-form data payload associated with the event.
 *
 * @example
 * trackMetaCustomEvent('DownloadedTemplate', { templateType: 'game_preview' });
 */
export function trackMetaCustomEvent(eventName: string, params?: MetaEventParams, eventOptions?: MetaEventOptions): void {
  if (!isMetaPixelEnabled()) {
    return;
  }

  initializeMetaPixel();
  const options = buildMetaEventOptions(eventOptions);
  if (options) {
    window.fbq?.("trackCustom", eventName, params ?? {}, options);
    return;
  }

  window.fbq?.("trackCustom", eventName, params ?? {});
}

/**
 * Retrieves the value of a specific cookie by its name.
 *
 * @param name - The exact name of the cookie to find.
 * @returns The decoded cookie value, or undefined if not found or not in a browser context.
 */
function getCookieValue(name: string): string | undefined {
  if (!isBrowser()) {
    return undefined;
  }

  const value = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${name}=`))
    ?.split("=")[1];

  return value ? decodeURIComponent(value) : undefined;
}

/**
 * Extracts Meta's first-party tracking cookies (fbp and fbc) from the browser.
 * Required for sending high-quality Server-Side API (CAPI) events.
 *
 * @returns An object containing the Facebook Browser ID (`fbp`) and Click ID (`fbc`) if available.
 */
export function getMetaBrowserIds(): { fbp?: string; fbc?: string } {
  return {
    fbp: getCookieValue("_fbp"),
    fbc: getCookieValue("_fbc"),
  };
}

/**
 * Generates a unique event identifier for Meta deduplication.
 * Essential when sending the same event from both the browser Pixel and Server-Side API (CAPI).
 *
 * @returns A globally unique string ID (UUID v4 if available, otherwise a random fallback).
 */
export function createMetaEventId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `meta_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function buildMetaEventOptions(eventOptions?: MetaEventOptions): { eventID: string } | undefined {
  if (!eventOptions?.eventId) {
    return undefined;
  }

  return {
    eventID: eventOptions.eventId,
  };
}
